<template>
  <transition
    is="tr"
    enter-active-class="animated fadeIn faster"
    leave-active-class="animated fadeOut faster"
    mode="out-in">
    <td
      colspan="15"
      class="is-clipped"
      v-if="!loading">
      <div
        class="is-padding-bottom"
        v-if="lot">
        <div class="columns is-gapless">
          <div class="column is-narrow is-w280p">
            <lot-plan
              :display-from-row="true"
              :lot="lot"></lot-plan>
          </div>
          <div class="column columns is-gapless is-multiline">
            <div class="column is-12 is-padding-right is-padding-left is-padding-top-xsmall is-padding-bottom-xsmall">
              <div class="is-uppercase is-size-5">
                <b>Lot {{ lot.ref }} : </b>
                <span class="has-text-grey">Dispositif(s) : </span>
                <span v-if="lot.tax_dispositifs">{{ lot.tax_dispositifs | prettyTax }}</span>
                <span v-else>NC</span>
                <span class="has-text-grey">/ Actabilité : </span>
                <span v-if="lot && lot.program.date_actabilite">{{ lot.program.date_actabilite | trimestre }}</span>
                <span v-else>NC</span>
                <span class="has-text-grey">/ Livraison : </span>
                <span v-if="lot && lot.program.date_livraison">{{ lot.program.date_livraison | trimestre }}</span>
                <span v-else>NC</span>
              </div>
            </div>
            <div class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-right is-padding-left is-border-right is-border-dotted">
              <div class="title is-5 is-margin-bottom-xsmall has-text-grey">
                Annexe / Stationnement / Mobilier
              </div>
              <div class="has-text-weight-medium is-size-6">
                <template v-if="annexes && !!Object.values(annexes).length">
                  <div
                    v-for="(items, type) in annexes"
                    :key="'annex-'+type"
                    class="has-text-grey has-text-weight-medium">
                    <span
                      class="has-text-black"
                      v-if="items.count > 1">{{ items.count }}</span>
                    <span class="has-text-black"> {{ type | annexTypeLabel | cap | pluralize(items.count > 1) }}</span>
                    <span
                      class="has-text-black"
                      v-if="(items.infos && !!items.infos.length) || items.surface"> : </span>
                    <span
                      v-for="i in items.count"
                      :key="i">
                      <span v-if="items.surface && items.surface[i - 1]">{{ items.surface[i - 1] }} m²</span>
                      <span
                        class="has-text-weight-light"
                        v-if="items.infos && !!items.infos[i - 1]">({{ items.infos[i - 1] }})</span>
                      <span v-if="items.count > 1 && i !== items.count"> / </span>
                    </span>
                  </div>
                </template>
                <div
                  v-for="(type, a) in parking_types"
                  :key="a">
                  <span
                    v-if="lot.parkings && lot.parkings.filter(x => x.nature === type).length"
                    class="has-text-black">
                    {{ lot.parkings.filter(x => x.nature === type).length }} {{ type | lotTypeLabel }}{{ lot.parkings.filter(x => x.nature === type).length > 1 ? 's':'' }} :
                  </span>
                  <template v-if="lot.parkings && lot.parkings.filter(x => x.nature === type).length">
                    <span
                      v-for="(p, i) in lot.parkings.filter(x => x.nature === type)"
                      :key="i"
                      class="has-text-grey has-text-weight-medium">
                      <span v-if="i > 0"> / </span>
                      <span v-if="p.parking_type && p.parking_type.length">
                        <span
                          v-for="(ptype, j) in p.parking_type"
                          :key="j">{{ j > 0 ? ', ':'' }}{{ ptype | lotSubtypeLabel }}</span>
                      </span>
                      <span
                        class="has-text-weight-light"
                        v-if="p.sales_mode && p.sales_mode.length || p.floor || p.info"> (
                        <span v-if="p.sales_mode && p.sales_mode.length">
                          <span
                            v-for="(psales_mode, j) in p.sales_mode"
                            :key="j">{{ j > 0 ? ', ':'' }}{{ psales_mode | salesmodeLabel }}</span>
                        </span>
                        <span v-if="p.floor">{{ p.sales_mode && p.sales_mode.length ? ', ':'' }}{{ p.floor | floorLabel }}</span>
                        <span v-if="p.info">{{ (p.sales_mode && p.sales_mode.length) || (p.floor) ? ', ':'' }}{{ p.info }} </span>
                        )
                      </span>
                      <span
                        v-if="p.sale_price_ttc"> {{ p.sale_price_ttc | prettyPrice }}€ </span>
                    </span>
                  </template>
                </div>
                <div>
                  <span
                    class="has-text-black"
                    v-if="lot.furnitures.length">
                    {{ lot.furnitures.length }} Meuble{{ lot.furnitures.length > 1 ? 's':'' }} :
                  </span>
                  <template v-if="lot && lot.furnitures && lot.furnitures.length">
                    <span
                      v-for="(furniture, idx) in lot.furnitures"
                      :key="'furniture' + idx"
                      class="has-text-grey">
                      <span class="has-text-weight-medium">{{ furniture.ref }}</span>
                      <span
                        class="has-text-weight-light"
                        v-if="furniture.info">
                        ({{ furniture.info }})
                      </span>
                      <span v-if="lot.furnitures.length > 1 && idx !== lot.furnitures.length - 1">/</span>
                      <span
                        class="has-text-grey"
                        v-if="furniture.sale_price_ttc"> {{ furniture.sale_price_ttc }}€</span>
                    </span>
                  </template>
                </div>
                <i v-if="(!annexes || !Object.values(annexes).length) && (!parkings || !parkings.totalCount)">NC</i>
              </div>
            </div>
            <div class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-left is-border-dotted">
              <div class="title is-5 is-margin-bottom-xsmall has-text-grey">
                Prix / Loyer
              </div>
              <div
                v-if="!lot.tva_on_marge"
                class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Prix de vente HT :
                  <span class="has-text-grey">
                    <span v-if="lot.sale_price_ht">{{ lot.sale_price_ht | prettyPrice }} €</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Prix de vente TTC :
                  <span class="has-text-grey">
                    <span v-if="lot.sale_price_ttc">{{ lot.sale_price_ttc | prettyPrice }} €</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
              <div
                v-if="lot.tva_on_marge"
                class="has-text-weight-medium">
                <span>
                  <i class="is-size-6">(TVA SUR MARGE APPLIQUÉE)</i>
                </span>
              </div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Loyer :
                  <span class="has-text-grey">
                    <span v-if="lot.rent">{{ lot.rent | prettyPrice }} €</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Rentabilité :
                  <span class="has-text-grey">
                    <span v-if="lot.profitability">{{ lot.profitability | prettyPrice }} %</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
            </div>
            <div
              class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-right is-padding-left"
              :class="{'is-border-left' : lot.pack_list && lot.pack_list.length !== 0}">
              <template v-if="lot.pack_list && lot.pack_list.length !== 0">
                <div class="title is-5 is-margin-bottom-xsmall has-text-grey">
                  Package
                </div>
                <template v-if="lot.pack_list && lot.pack_list.length">
                  <div
                    class="has-text-weight-medium is-size-6"
                    v-for="(pack, i) in lot.pack_list"
                    :key="i">
                    <span class="has-text-black">{{ pack.name }} TTC :
                      <span class="has-text-grey">
                        <span v-if="pack.sale_price_ttc">{{ pack.sale_price_ttc | prettyPrice }} €</span>
                        <span v-else><i>NC</i></span>
                      </span>
                    </span>
                  </div>
                </template>
              </template>
            </div>
            <div class="column is-12 is-margin-top-small is-padding-top-xsmall is-padding-bottom-xsmall is-padding-left">
              <div class="buttons">
                <a
                  :id="`addToWishlistButton-${index}`"
                  v-if="!wishlisted"
                  @click.prevent.stop="addToWishlist"
                  @mouseover="hoverWishlist = true"
                  @mouseleave="hoverWishlist = false"
                  class="button is-small is-square is-border is-light is-transform-none is-lGradient">
                  <span class="icon is-small"><i
                    class="fa-star"
                    :class="hoverWishlist ? 'fas' : 'fal'"></i></span>
                </a>
                <a
                  v-else
                  :id="`deleteFromWishlistButton-${index}`"
                  @click.prevent.stop="$emit('deleteFromWishlist')"
                  class="button is-small is-square is-border is-light is-transform-none is-lGradient">
                  <span class="icon is-small"><i class="fas fa-star"></i></span>
                </a>
                <router-link
                  :to="{name: 'ProgramVue', params: {id: lot.program.pk}}"
                  v-if="!$route.name.includes('Program')"
                  class="button is-small is-border is-light is-transform-none is-lGradient">
                  <span>Fiche programme</span>
                </router-link>
                <router-link
                  :to="{name: 'LotVue', params: {id: lot.pk}}"
                  v-if="$route.params.id !== lotPk"
                  class="button is-small is-primary is-transform-none is-lGradient">
                  <span>Fiche détaillée du lot</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </td>
  </transition>
</template>

<script>
import LotMixin from '../lots/LotMixin'
import { LOT_TYPES } from '../../mixins/DefaultValues'

export default {
  name: 'LotRowInfos',
  components: {
    LotPlan: () => import('@/components/lots/LotPanels/LotPlan')
  },
  mixins: [LotMixin],
  props: {
    index: Number
  },
  data () {
    return {
      hoverWishlist: false,
      lotLight: true,
      parking_types: []
    }
  },
  watch: {
    loading (v) {
      this.$emit('loading', v)
    }
  },
  created () {
    this.getLot(this.lot.pk)
    for (const [key, value] of Object.entries(LOT_TYPES)) {
      if (value.show.includes('parking')) this.parking_types.push(key)
    }
  }
}
</script>
